import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ussd, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Select, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../i18n/I18nProvider';
import { StatusPill, type PillTone } from '../../components/panel';
import { formatDate } from '../../lib/format';
import { formatCredits } from '../../lib/credits';
import { useUssdStatus } from '../../lib/ussd';
import { useIsOrgAdmin } from '../../app/components/RoleGate';

const MUTED = 'var(--rr-color-text-muted)';
const CODE_TONE: Record<ussd.UssdCodeStatus, PillTone> = { live: 'active', suspended: 'paused', released: 'neutral' };
const APP_TONE: Record<ussd.UssdApplicationStatus, PillTone> = { submitted: 'submitted', approved: 'approved', rejected: 'rejected', withdrawn: 'neutral' };

/** A refusal the person can do something about, in their own language; `topup` adds the link to buy credit. */
function refusal(t: Translate, err: unknown): { text: string; topup?: boolean } {
  if (isApiError(err)) {
    switch (err.code) {
      case 'USSD_TERMS_REQUIRED': return { text: t('channels.ussd.error.terms') };
      case 'USSD_NO_CREDIT': return { text: t('channels.ussd.error.noCredit'), topup: true };
      case 'USSD_APPLICATION_OPEN': return { text: t('channels.ussd.error.open') };
      case 'USSD_APPLICATIONS_CLOSED': return { text: t('channels.ussd.error.closed') };
      case 'USSD_BILLING_UNAVAILABLE': return { text: t('channels.ussd.error.billing') };
      case 'USSD_APPLICATION_DECIDED': return { text: t('channels.ussd.error.decided') };
      case 'ADMIN_ROLE_REQUIRED': return { text: t('channels.ussd.error.adminOnly') };
      default: break;
    }
  }
  return { text: err instanceof Error ? err.message : t('common.unknownError') };
}

/**
 * USSD on the Channels page (4 October 2026).
 *
 * A business applies for a code here: an extension on the platform's shared
 * code (approved in days) or a dedicated code of its own (weeks). The page
 * says what it costs before anything is asked: the set-up fee held now from
 * bought credits, the rest of the month's rental at approval, the rental on
 * each 1st and a charge per session. Once the code is live, a flow answers it:
 * the code is given to a bot under Settings, the same as a WhatsApp number.
 *
 * Shown when USSD is open, or when the business already has a code or an
 * application. Agents see it read-only; the service refuses them anyway.
 */
export function UssdSection() {
  const t = useT();
  const qc = useQueryClient();
  const canEdit = useIsOrgAdmin();
  const { status, available, shown, isPending } = useUssdStatus();
  const [applying, setApplying] = useState(false);

  // Every application, decided ones too, so a rejection and its reason stay in view.
  const apps = useQuery({
    queryKey: ['ussd-applications'],
    queryFn: () => ussd.listApplications(),
    enabled: shown,
    retry: false,
    refetchInterval: 30_000
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['ussd-status'] });
    void qc.invalidateQueries({ queryKey: ['ussd-applications'] });
    void qc.invalidateQueries({ queryKey: ['ussd-codes'] });
  };

  const withdraw = useMutation({
    mutationFn: (a: ussd.UssdApplication) => ussd.withdraw(a.id),
    onSuccess: () => { refresh(); notifySuccess(t('channels.ussd.withdrawn'), t('channels.ussd.title')); },
    onError: (e) => notifyError(refusal(t, e).text, t('channels.ussd.title'))
  });

  if (isPending || !shown || !status) return null;

  const codes = status.codes;
  const rows = apps.data?.data ?? status.applications;
  const waiting = rows.some((a) => a.status === 'submitted');
  const anyOpen = available && status.kinds.some((k) => k.open);

  return (
    <Card withBorder data-testid="ussd-section">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <span className="rr-channels__mark rr-channels__mark--ussd" aria-hidden />
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Title order={4}>{t('channels.ussd.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('channels.ussd.intro')}</Text>
            </Stack>
          </Group>
          {canEdit && anyOpen && (
            <Button size="sm" disabled={waiting} onClick={() => setApplying(true)}>{t('channels.ussd.apply')}</Button>
          )}
        </Group>

        {!available && <Alert color="info">{t('channels.ussd.closed')}</Alert>}
        {canEdit && anyOpen && waiting && <Text role="caption" c={MUTED}>{t('channels.ussd.oneAtATime')}</Text>}

        {/* The codes the business has. */}
        {codes.length > 0 && (
          <Stack gap="xs">
            <Text role="label">{t('channels.ussd.codes')}</Text>
            {codes.map((c) => (
              <div key={c.id} className="rr-channels__ussd-row" data-testid="ussd-code">
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Group gap="xs" wrap="wrap">
                    <code className="rr-channels__ussd-code">{c.code}</code>
                    <StatusPill tone={CODE_TONE[c.status]}>{t(`channels.ussd.codeStatus.${c.status}`)}</StatusPill>
                    <Text role="caption" c={MUTED}>{t(`channels.ussd.kind.${c.kind}`)}</Text>
                  </Group>
                  {c.status === 'suspended' && c.suspended_reason && <Text role="caption">{c.suspended_reason}</Text>}
                  {c.overdue_since && <Text role="caption" c="var(--rr-color-danger)">{t('channels.ussd.overdue', { when: formatDate(c.overdue_since) })}</Text>}
                  {c.status !== 'released' && c.next_rental_at && !c.overdue_since && (
                    <Text role="caption" c={MUTED}>{t('channels.ussd.nextRental', { when: formatDate(c.next_rental_at) })}</Text>
                  )}
                </Stack>
              </div>
            ))}
          </Stack>
        )}

        {/* Applications: waiting ones can be withdrawn, decided ones say how. */}
        {rows.length > 0 && (
          <Stack gap="xs">
            <Text role="label">{t('channels.ussd.applications')}</Text>
            {rows.map((a) => (
              <div key={a.id} className="rr-channels__ussd-row" data-testid="ussd-application">
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Group gap="xs" wrap="wrap">
                    <Text role="bodySmall" fw={600}>{a.code ?? t(`channels.ussd.kind.${a.kind}`)}</Text>
                    <StatusPill tone={APP_TONE[a.status]}>{t(`channels.ussd.appStatus.${a.status}`)}</StatusPill>
                  </Group>
                  <Text role="caption" c={MUTED}>
                    {a.status === 'submitted'
                      ? t(a.fee_held ? 'channels.ussd.line.held' : 'channels.ussd.line.submitted', { when: formatDate(a.created_at), fee: formatCredits(a.fee_minor, t) })
                      : t(`channels.ussd.line.${a.status}`, { when: formatDate(a.decided_at ?? a.created_at) })}
                  </Text>
                  {a.preferred_digits && a.status === 'submitted' && (
                    <Text role="caption" c={MUTED}>{t('channels.ussd.line.digits', { digits: a.preferred_digits })}</Text>
                  )}
                  {a.status === 'rejected' && a.reject_reason && <Text role="caption">{a.reject_reason}</Text>}
                </Stack>
                {canEdit && a.status === 'submitted' && (
                  <Button
                    size="xs" variant="subtle" color="red"
                    loading={withdraw.isPending && withdraw.variables?.id === a.id}
                    onClick={() => openConfirmModal({
                      title: t('channels.ussd.withdrawTitle'),
                      children: <Text role="bodySmall">{t('channels.ussd.withdrawBody', { fee: formatCredits(a.fee_minor, t) })}</Text>,
                      labels: { confirm: t('channels.ussd.withdraw'), cancel: t('action.cancel') },
                      onConfirm: () => withdraw.mutate(a)
                    })}
                  >
                    {t('channels.ussd.withdraw')}
                  </Button>
                )}
              </div>
            ))}
          </Stack>
        )}

        {codes.length === 0 && rows.length === 0 && <Text role="bodySmall" c={MUTED}>{t('channels.ussd.none')}</Text>}

        {/* How a code comes to answer: a flow, and the code given to its bot. */}
        <Stack gap={4}>
          <Text role="label">{t('channels.ussd.how.title')}</Text>
          <Text role="bodySmall">{t('channels.ussd.how.flow')} <Link to="/flows">{t('nav.flows')}</Link></Text>
          <Text role="bodySmall">{t('channels.ussd.how.link')} <Link to="/settings/assistant">{t('settings.assistant.title')}</Link></Text>
          <Text role="caption" c={MUTED}>{t('channels.ussd.how.limits')}</Text>
        </Stack>
      </Stack>

      {canEdit && (
        <ApplyDrawer
          opened={applying}
          status={status}
          onClose={() => setApplying(false)}
          onDone={() => { refresh(); setApplying(false); notifySuccess(t('channels.ussd.sent'), t('channels.ussd.title')); }}
        />
      )}
    </Card>
  );
}

function ApplyDrawer({ opened, status, onClose, onDone }: { opened: boolean; status: ussd.UssdStatus; onClose: () => void; onDone: () => void }) {
  const t = useT();
  const firstOpen = status.kinds.find((k) => k.open)?.kind ?? 'extension';
  const [kind, setKind] = useState<ussd.UssdCodeKind>(firstOpen);
  const [digits, setDigits] = useState('');
  const [digitsError, setDigitsError] = useState('');
  const [purpose, setPurpose] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [problem, setProblem] = useState<{ text: string; topup?: boolean } | null>(null);

  useEffect(() => {
    if (!opened) return;
    setKind(firstOpen); setDigits(''); setDigitsError(''); setPurpose(''); setAgreed(false); setProblem(null);
  }, [opened, firstOpen]);

  const quote = useQuery({
    queryKey: ['ussd-quote', kind],
    queryFn: () => ussd.quote(kind),
    enabled: opened,
    retry: false
  });
  const q = quote.data?.data;

  const send = useMutation({
    mutationFn: () => ussd.apply({ kind, preferred_digits: digits.trim() || undefined, purpose: purpose.trim(), accept_terms: agreed }),
    onSuccess: onDone,
    onError: (e) => setProblem(refusal(t, e))
  });

  function submit() {
    setProblem(null);
    if (digits.trim() && !/^[1-9]\d{0,5}$/.test(digits.trim())) {
      setDigitsError(t('channels.ussd.form.digitsInvalid'));
      return;
    }
    send.mutate();
  }

  const purposeLength = purpose.trim().length;
  const ready = !!q?.open && purposeLength >= 10 && purposeLength <= 1000 && agreed && !send.isPending;
  const shared = status.shared_code ?? '*120*1234#';
  const example = shared.replace(/#$/, '*56#');

  return (
    <Drawer opened={opened} onClose={onClose} title={t('channels.ussd.form.title')} position="right" size="lg">
      <Stack gap="md" data-testid="ussd-apply-form">
        <Select
          label={t('channels.ussd.form.kind')}
          description={kind === 'extension' ? t('channels.ussd.form.kindHint.extension', { example }) : t('channels.ussd.form.kindHint.dedicated')}
          value={kind}
          onChange={(e) => setKind(e.currentTarget.value as ussd.UssdCodeKind)}
          data={status.kinds.map((k) => ({
            value: k.kind,
            label: k.open ? t(`channels.ussd.kind.${k.kind}`) : t('channels.ussd.form.kindClosed', { kind: t(`channels.ussd.kind.${k.kind}`) }),
            disabled: !k.open
          }))}
        />
        <TextInput
          label={t('channels.ussd.form.digits')}
          description={t('channels.ussd.form.digitsHint')}
          value={digits}
          inputMode="numeric"
          maxLength={6}
          error={digitsError || undefined}
          onChange={(e) => { setDigits(e.currentTarget.value); setDigitsError(''); }}
        />
        <Textarea
          label={t('channels.ussd.form.purpose')}
          description={t('channels.ussd.form.purposeHint', { count: purposeLength })}
          value={purpose}
          maxLength={1000}
          minRows={3}
          onChange={(e) => setPurpose(e.currentTarget.value)}
        />

        {/* The quote, before anything is asked of them. */}
        {quote.isPending && <Skeleton height={120} />}
        {quote.error && <Alert color="error">{t('channels.ussd.form.quoteError')}</Alert>}
        {q && !q.open && <Alert color="warning">{t('channels.ussd.error.closed')}</Alert>}
        {q && q.open && (
          <Stack gap="xs" data-testid="ussd-quote">
            <Text role="label">{t('channels.ussd.form.cost')}</Text>
            <dl className="rr-channels__facts">
              <dt>{t('channels.ussd.form.setup')}</dt><dd>{formatCredits(q.setup_minor, t)}</dd>
              <dt>{t('channels.ussd.form.firstMonth', { period: q.first_month_period })}</dt><dd>{formatCredits(q.first_month_minor, t)}</dd>
              <dt>{t('channels.ussd.form.monthly', { date: q.next_rental_on })}</dt><dd>{formatCredits(q.monthly_minor, t)}</dd>
              <dt>{t('channels.ussd.form.session')}</dt><dd>{formatCredits(q.session_minor, t)}</dd>
            </dl>
            <Text role="caption" c={MUTED}>{t('channels.ussd.form.costNote')} <Link to="/billing/topup">{t('channels.ussd.form.topup')}</Link></Text>
          </Stack>
        )}

        <Checkbox
          checked={agreed}
          onChange={(v) => setAgreed(v)}
          label={t('channels.ussd.form.agree', { fee: formatCredits(q?.setup_minor ?? 0, t) })}
          description={q?.terms_version ? t('channels.ussd.form.termsVersion', { version: q.terms_version }) : undefined}
        />

        {problem && (
          <Alert color="error" role="alert">
            {problem.text}{problem.topup && <> <Link to="/billing/topup">{t('channels.ussd.form.topup')}</Link></>}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={send.isPending} disabled={!ready} onClick={submit}>{t('channels.ussd.form.send')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
